import { Select } from 'antd';
import styled, { createGlobalStyle } from 'styled-components';

import InputOption from '../../molecules/InputOption';

import { breakpoint, sizeUp } from '~/theme';
import CommonButton from '~atoms/CommonButton';
import PageTitle from '~atoms/PageTitle';
import { headerVars } from '~organisms/Header';

const FLEX_START = 'flex-start';
const BREAK_WORD = 'break-word';

export const PageGlobalStyles = createGlobalStyle(({ theme }) => ({
  body: {
    backgroundColor: theme.palette.metallicBlack,
  },
}));

export const GlobalSkeletonStyles = createGlobalStyle`
  @keyframes pulse {
    0% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
    100% {
      opacity: 1;
    }
  }
`;

export const PageWrapper = styled.div({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',

  [breakpoint.media.minMf.md]: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    height: `calc(100vh - ${headerVars.headerHeight})`,
  },
});

export const LeftColumn = styled.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  flex: '0 0 auto',
  boxSizing: 'border-box',
  width: '100%',
  minWidth: 0,
  fontSize: theme.typography.size16,
  paddingInline: sizeUp('20rem', ['md', '40rem']),
  paddingBlock: sizeUp('40rem', ['xl', '80rem']),
  backgroundColor: theme.palette.metallicBlack,

  [breakpoint.media.minMf.md]: {
    paddingInline: sizeUp('16rem', ['xl', '64rem']),
  },
}));

const SharedSectionTitle = styled(PageTitle)({
  fontWeight: 'normal',
  fontSize: sizeUp('18rem', ['xl', '24rem']),
  lineHeight: sizeUp('24rem', ['xl', '32rem']),
  letterSpacing: 0,
});

export const HeroTitle = styled(SharedSectionTitle)({
  marginBottom: sizeUp('16rem', ['xl', '22rem']),
});

export const HeroDescription = styled.span(({ theme }) => ({
  fontFamily: theme.typography.family.arial,
  fontSize: theme.typography.size16,
  color: theme.palette.artichoke,
  lineHeight: '18rem',
  marginBottom: sizeUp('24rem', ['xl', '80rem']),
  letterSpacing: 0,
  whiteSpace: 'pre-line',
}));

export const HeroImage = styled.div({
  display: 'block',
  width: '100%',
  aspectRatio: '16 / 9',
  objectFit: 'cover',

  backgroundRepeat: 'no-repeat',
  backgroundSize: 'cover',
  backgroundPosition: 'center',

  [breakpoint.media.minMf.md]: {
    height: '100%',
    aspectRatio: 'auto',
  },
});

export const Title = styled(SharedSectionTitle)({
  marginBottom: sizeUp('20rem', ['xl', '30rem']),
});

// Grid layout so the ButtonContainer (button + checkbox stack) lives in its
// own column beside the RowsContainer instead of inside the first Row. That
// prevents the first Row from inheriting the ButtonContainer's extra height
// and creating a larger gap before the next Row.
export const ContentWrapper = styled.div({
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr)',
  gridTemplateRows: 'auto auto',
  rowGap: '8rem',
  width: '100%',
  minWidth: 0,

  [breakpoint.media.min.xs]: {
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    gridTemplateRows: 'auto',
    columnGap: 'clamp(24rem, calc((100vw - 1080rem) * 0.3), 60rem)',
    rowGap: 0,
  },
});

export const RowsContainer = styled.div({
  gridColumn: '1',
  gridRow: '1',
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
});

export const Row = styled.div(({ theme }) => ({
  display: 'flex',
  // nowrap so long values wrap inside RowValue instead of the whole item
  // wrapping onto a new flex line below the label.
  flexWrap: 'nowrap',
  gap: sizeUp('12rem', ['xl', '20rem']),
  alignItems: 'center',
  lineHeight: '32rem',
  color: theme.palette.white,
  overflowWrap: BREAK_WORD,
  wordBreak: BREAK_WORD,
  minWidth: 0,

  [breakpoint.media.max.xs]: {
    flexDirection: 'column',
    alignItems: FLEX_START,
    gap: '4rem',
    lineHeight: '22rem',
  },
}));

export const RowTitle = styled.span(({ theme }) => ({
  minWidth: '128rem',
  textAlign: 'start',
  color: theme.palette.white,
  lineHeight: '18px',
  letterSpacing: 0,
  flexShrink: 0,

  [breakpoint.media.max.xs]: {
    minWidth: 0,
    textAlign: 'start',
  },
}));

export const RowValue = styled.span({
  flex: '1 1 auto',
  minWidth: 0,
  overflowWrap: BREAK_WORD,
  wordBreak: BREAK_WORD,
});

export const ButtonContainer = styled.div({
  gridColumn: '1',
  gridRow: '2',
  display: 'flex',
  flexDirection: 'column',
  gap: '12rem',
  alignItems: FLEX_START,
  // Align the button's top edge with the first Row's top (Estimate Number
  // dropdown), not with the cell's middle.
  alignSelf: 'start',

  [breakpoint.media.min.xs]: {
    gridColumn: '2',
    gridRow: '1',
  },
});

export const SaveEstimateButton = styled(CommonButton)(({ theme }) => ({
  fontSize: sizeUp(theme.typography.size16, ['lg', theme.typography.size20]),
  whiteSpace: 'nowrap',
}));

export const CheckboxContainer = styled.div(({ theme }) => ({
  color: theme.palette.white,
}));

export const CurrentDesignCheckbox = styled(InputOption)(({ theme }) => ({
  '#useCurrentDesign::before': {
    border: `1rem solid ${theme.palette.white}`,
    backgroundColor: theme.palette.metallicBlack,
  },

  '#useCurrentDesign:focus::before': {
    outline: `2rem solid ${theme.palette.white}`,
  },

  '#useCurrentDesign:focus:checked::before': {
    outline: `2rem solid ${theme.palette.black}`,
  },

  '#useCurrentDesign::after': {
    backgroundColor: theme.palette.metallicBlack,
    outline: `2rem solid ${theme.palette.white}`,
  },

  '#useCurrentDesign:checked::after': {
    backgroundColor: theme.palette.white,
    outline: 'none',
    backgroundImage: `url("data:image/svg+xml,<svg viewBox='0 0 14 14' fill='none' xmlns='http://www.w3.org/2000/svg'><path d='M3 7l3 3 5-5' stroke='${theme.palette.metallicBlack.replace('#', '%23')}' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/></svg>")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    backgroundSize: 'contain',
  },
}));

export const SelectList = styled(Select<string>)(({ theme }) => ({
  minWidth: '150rem',
  maxWidth: '100%',

  '& .ant-select-selector': {
    borderColor: `${theme.palette.white} !important`,
    backgroundColor: 'transparent !important',
  },
  '& .ant-select-selection-item': {
    fontSize: theme.typography.size16,
    fontFamily: theme.typography.family.arial,
    color: `${theme.palette.white} !important`,
  },
  '& .ant-select-arrow': {
    color: theme.palette.white,
  },
}));

export const SkeletonItem = styled.div(({ theme }) => ({
  '&[data-height="large"]': {
    height: '32rem',
  },
  '&[data-height="medium"]': {
    height: '16rem',
  },
  '&[data-width="small"]': {
    width: '120rem',
  },
  '&[data-width="medium"]': {
    width: '180rem',
  },
  '&[data-width="large"]': {
    width: '250rem',
    maxWidth: '100%',
  },
  backgroundColor: theme.palette.chineseWhite,
  borderRadius: '0.25rem',
  animation: 'pulse 1.5s ease-in-out infinite',
}));

export const SkeletonImage = styled.div(({ theme }) => ({
  width: '100%',
  aspectRatio: '16 / 9',
  backgroundColor: theme.palette.chineseWhite,
  borderRadius: '8rem',
  animation: 'pulse 1.5s ease-in-out infinite',

  [breakpoint.media.minMf.md]: {
    height: '100%',
    aspectRatio: 'auto',
    borderRadius: 0,
  },
}));

export const StoresListContainer = styled.div({
  marginTop: '32rem',
});
